import { CAP_POS_DETERMINE_READER, checkCapFlag } from 'bl/captures.bl';
import determineErrorMessage from 'bl/response/determineErrorMessage.bl';
import SENTRY from 'config/sentry.singleton';
import SF from 'constants/salonFlag.const';
import fromBeToBody from 'utils/converters/backend/fromBeToBody.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import withOptions from 'utils/functions/withOptions.util';
import logger from 'utils/logger.util';

const CTAG = 'determineAndStorePaymentIntentId';
const L = logger(CTAG);

const captureFailedResponse = withOptions(({ name, readerId, errorMessage, result, flags }) => {
  const message = CAP_POS_DETERMINE_READER;
  const prefix = SF.determineAndStorePaymentIntentId;

  if (checkCapFlag({ prefix, flags }).warn) return message;

  const logger = [CTAG, name];
  const tags = { function: name, readerId };
  const extra = { ...tags, errorMessage, result };
  return SENTRY.warn({ logger, tags, extra, message });
});

const determineCorrectId = ({ isError, isPrevious, oldIntentId, newIntentId, isOldUsable }) => {
  if (isError) return null;

  // NOTE: in some cases where feDelPosIntentId() isn't called and we'd get the old instead of the new ID
  // SEE: https://getslick.atlassian.net/browse/GS-11991
  // SEE: https://getslick.atlassian.net/browse/GS-11856
  if (isOldUsable === false) return newIntentId;

  return isPrevious ? oldIntentId : newIntentId;
};

const determineAndStorePaymentIntentId = async (options) => {
  const { amount, appointmentId, beInitReaderPayment, clientId, feSetPosIntentId, flags, items, isOldUsable, paymentOrigin, readerId } = options ?? {};
  const { intentId: oldIntentId, amount: amountFromPos } = options?.pos ?? {};

  // repeated attempt having the same amount should use the same payment intent ID as per Stripe requirement
  const isPrevious = fromAnyToBool(oldIntentId && amountFromPos === amount);
  if (isPrevious) L.info('()', 'Found oldIntentId:', oldIntentId, 'for the same amount:', amount);

  const initResult = await beInitReaderPayment({
    amount,
    appointmentId,
    clientId,
    flags,
    items,
    paymentIntentId: isPrevious ? oldIntentId : null,
    paymentOrigin,
    readerId,
  });
  L.debug('()', 'initResult:', initResult);

  const { intent_id: newIntentId } = fromBeToBody(initResult);
  const errorMessage = determineErrorMessage(initResult);
  const isError = !initResult.is.ok || fromAnyToBool(errorMessage);

  if (isError) captureFailedResponse({ name: CTAG, readerId, errorMessage, result: initResult, flags });

  const paymentIntentId = determineCorrectId({ isError, isPrevious, oldIntentId, newIntentId, isOldUsable });

  if (!isPrevious && !isError) {
    L.info('()', 'Setting newIntentId:', newIntentId, '...');
    feSetPosIntentId(newIntentId);
  }

  const result = { paymentIntentId, errorMessage, oldIntentId, newIntentId, isPrevious, isError };
  L.info('()', 'Returning result:', result);
  return result;
};

// noinspection JSUnusedGlobalSymbols
export default determineAndStorePaymentIntentId;
